import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ApiError } from '../../lib/api-client';
import { useToast } from '../../lib/toast';
import { PatientUhidAutocomplete } from '../../components/ui/PatientUhidAutocomplete';
import { FieldError } from '../../components/ui/FieldError';
import { usePatientUhidSearch } from '../../lib/patient-uhid-search';
import { normalizePhoneInput } from '../../lib/phone-input';
import { useCreateDialysisAdmission, useDialysisLookup } from './dialysis.queries';
import { useIpdDaycareAdmission } from '../ipd-daycare/ipd-daycare.queries';
import {
  AdmissionInfoFields,
  FieldLabel,
  LookupSelect,
  PatientDemographicFields,
  PatientIdentityFields,
  ResponsiblePersonFields,
} from './dialysis.shared';
import { blankPatientFields, blankResponsiblePerson, dateTimeInput, patientFieldsFromOption, timeInput } from './dialysis.utils';
import type { CreateDialysisAdmissionPayload, PatientFormFields } from './types';

interface AdmissionInfoState {
  underDoctor: string | null;
  dialysisMachine: string | null;
  startTime: string;
  endTime: string;
  bedCategory: string | null;
  bed: string | null;
  referredBy: string | null;
}

const blankAdmissionInfo = (): AdmissionInfoState => ({
  underDoctor: null, dialysisMachine: null, startTime: timeInput(), endTime: timeInput(5), bedCategory: null, bed: null, referredBy: null,
});

const validateDialysisFields = (
  admissionDate: string,
  insuranceType: string,
  patient: PatientFormFields,
  admissionInfo: AdmissionInfoState,
  responsiblePerson: ReturnType<typeof blankResponsiblePerson>,
) => {
  const errors: Record<string, string> = {};
  if (!admissionDate) errors.admissionDate = 'Admission Date & time is required.';
  if (!admissionInfo.dialysisMachine) errors.dialysisMachine = 'Dialysis Machine is required.';
  if (!insuranceType) errors.insuranceType = 'Patient Type is required.';
  if (!patient.name.trim()) errors.name = "Patient's name is required.";
  if ((patient.phone ?? '').trim() && !/^\d{10}$/.test((patient.phone ?? '').trim())) errors.phone = 'Enter a 10 digit phone number.';
  if ((patient.guardianContactNo ?? '').trim() && !/^\d{10}$/.test((patient.guardianContactNo ?? '').trim())) errors.guardianContactNo = 'Enter a 10 digit phone number.';
  if (!(patient.guardianName ?? '').trim()) errors.guardianName = 'Guardian Name is required.';
  if (!patient.relation) errors.relation = 'Relation is required.';
  if (!(patient.address ?? '').trim()) errors.address = 'Address is required.';
  if (!(patient.state ?? '').trim()) errors.state = 'State is required.';
  if (!admissionInfo.underDoctor) errors.underDoctor = 'Under Doctor is required.';
  if (!admissionInfo.bedCategory) errors.bedCategory = 'Bed Category is required.';
  if (!admissionInfo.bed) errors.bed = 'Bed is required.';
  if (!admissionInfo.startTime) errors.startTime = 'Start Time is required.';
  if (!admissionInfo.endTime) errors.endTime = 'End Time is required.';
  if (admissionInfo.startTime && admissionInfo.endTime && admissionInfo.endTime <= admissionInfo.startTime) {
    errors.endTime = 'End Time must be later than Start Time.';
  }
  if (!admissionInfo.referredBy) errors.referredBy = 'Referred By is required.';
  if (responsiblePerson.phone.trim() && !/^\d{10}$/.test(responsiblePerson.phone.trim())) errors.responsiblePersonPhone = 'Enter a 10 digit phone number.';
  return errors;
};

export const DialysisRegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const mutation = useCreateDialysisAdmission();
  const sourceAdmissionId = searchParams.get('from-admission');
  const sourceQuery = useIpdDaycareAdmission(sourceAdmissionId ?? undefined);

  const [admissionDate, setAdmissionDate] = useState(dateTimeInput);
  const [insuranceType, setInsuranceType] = useState('');
  const [insuranceNumber, setInsuranceNumber] = useState('');

  const [patientSearch, setPatientSearch] = useState('');
  const debouncedPatientSearch = usePatientUhidSearch(patientSearch);
  const patients = useDialysisLookup('patients', debouncedPatientSearch);
  const [patient, setPatient] = useState<PatientFormFields>(blankPatientFields);
  const states = useDialysisLookup('states');
  const districts = useDialysisLookup('districts', '', { state: patient.state ?? '' });

  const [admissionInfo, setAdmissionInfo] = useState<AdmissionInfoState>(blankAdmissionInfo);
  const doctors = useDialysisLookup('doctors');
  const dialysisMachines = useDialysisLookup('dialysis-machines');
  const wards = useDialysisLookup('wards');
  const beds = useDialysisLookup('beds', '', { ward: admissionInfo.bedCategory ?? '' });
  const referrals = useDialysisLookup('referrals');
  const tpas = useDialysisLookup('tpas');

  useEffect(() => {
    if (admissionInfo.bedCategory || !wards.data) return;
    const dialysisWard = wards.data.find((ward) => ward.label.trim().toLowerCase() === 'dialysis');
    if (dialysisWard) setAdmissionInfo((current) => current.bedCategory ? current : { ...current, bedCategory: dialysisWard.id });
  }, [admissionInfo.bedCategory, wards.data]);

  const [responsiblePerson, setResponsiblePerson] = useState(blankResponsiblePerson);
  const [showErrors, setShowErrors] = useState(false);
  const errors = showErrors ? validateDialysisFields(admissionDate, insuranceType, patient, admissionInfo, responsiblePerson) : {};

  const prefillNotified = useRef(false);
  useEffect(() => {
    const row = sourceQuery.data;
    if (!row) return;
    setPatientSearch(row.patientSnapshot.uhid ?? '');
    setPatient({
      patientId: row.patient.id,
      uhid: row.patientSnapshot.uhid,
      name: row.patientSnapshot.name,
      phone: row.patientSnapshot.phone ? normalizePhoneInput(row.patientSnapshot.phone) : null,
      gender: row.patientSnapshot.gender,
      maritalStatus: row.patientSnapshot.maritalStatus,
      guardianName: row.patientSnapshot.guardianName,
      relation: row.patientSnapshot.relation,
      guardianContactNo: null,
      dateOfBirth: row.patientSnapshot.dateOfBirth,
      ageYears: row.patientSnapshot.ageYears,
      ageMonths: row.patientSnapshot.ageMonths,
      ageDays: row.patientSnapshot.ageDays,
      address: row.patientSnapshot.address,
      state: row.patientSnapshot.state ?? 'West Bengal (WB)',
      district: row.patientSnapshot.district,
      pinCode: row.patientSnapshot.pinCode,
      aadhaar: row.patientSnapshot.aadhaar,
    });
    setInsuranceType(row.insuranceType ?? '');
    setInsuranceNumber(row.insuranceNumber ?? '');
    setResponsiblePerson({
      name: row.responsiblePerson.name ?? '',
      relation: row.responsiblePerson.relation ?? '',
      phone: row.responsiblePerson.phone ? normalizePhoneInput(row.responsiblePerson.phone) : '',
      age: row.responsiblePerson.age,
      address: row.responsiblePerson.address ?? '',
    });
    if (!prefillNotified.current) {
      prefillNotified.current = true;
      showToast(`Patient details imported from IPD/Daycare admission ${row.ipdNumber}.`, 'success');
    }
  }, [sourceQuery.data, showToast]);
  useEffect(() => {
    const row = sourceQuery.data;
    if (!row) return;
    setAdmissionInfo((current) => ({
      ...current,
      underDoctor: current.underDoctor ?? doctors.data?.find((item) => item.id === row.underDoctor?.id)?.id ?? null,
      referredBy: current.referredBy ?? referrals.data?.find((item) => item.id === row.referredBy?.id)?.id ?? null,
    }));
  }, [sourceQuery.data, doctors.data, referrals.data]);
  useEffect(() => {
    if (sourceQuery.isError && !prefillNotified.current) {
      prefillNotified.current = true;
      showToast('The IPD/Daycare admission details could not be loaded for prefill.', 'warning');
    }
  }, [sourceQuery.isError, showToast]);

  const choosePatient = (id: string) => {
    const option = patients.data?.find((item) => item.id === id);
    if (!option) { setPatient(blankPatientFields()); return; }
    setPatient(patientFieldsFromOption(option));
  };
  const changePatientSearch = (value: string) => { setPatientSearch(value); if (patient.patientId) setPatient(blankPatientFields()); };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setShowErrors(true);
    if (Object.keys(validateDialysisFields(admissionDate, insuranceType, patient, admissionInfo, responsiblePerson)).length > 0) return;
    const payload: CreateDialysisAdmissionPayload = {
      ...patient,
      dialysisType: sourceQuery.data ? 'MOVED BY IPD' : 'DIRECT',
      admissionDate,
      insuranceType,
      insuranceNumber: insuranceNumber || null,
      underDoctor: admissionInfo.underDoctor,
      dialysisMachine: admissionInfo.dialysisMachine,
      startTime: admissionInfo.startTime,
      endTime: admissionInfo.endTime,
      bedCategory: admissionInfo.bedCategory,
      bed: admissionInfo.bed,
      referredBy: admissionInfo.referredBy,
      responsiblePerson,
    };
    try {
      const created = await mutation.mutateAsync(payload);
      showToast('Dialysis admission saved successfully.', 'success');
      void navigate(`/dialysis/${created.id}`);
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'Dialysis admission could not be saved.', 'error');
    }
  };

  const busy = mutation.isPending;
  return (
    <div className="page-stack ipd-daycare-page">
      <div className="page-heading"><div><p className="eyebrow">Dialysis</p><h1>Dialysis Admission</h1><p>Patient details, admission information, and responsible person.</p></div><Link className="button button-secondary" to="/dialysis">Back to Dialysis List</Link></div>
      <form className="content-card ipd-daycare-registration-card" noValidate onSubmit={(event) => void submit(event)}>
<section className="ipd-daycare-section">
          <div className="dialysis-top-row">
            <label><FieldLabel required>Admission Date & time</FieldLabel><input type="datetime-local" value={admissionDate} onChange={(event) => setAdmissionDate(event.target.value)} required aria-invalid={Boolean(errors.admissionDate)} /><FieldError message={errors.admissionDate} /></label>
            <label><FieldLabel required>Dialysis Machine</FieldLabel><LookupSelect id="dialysis-machine-top" value={admissionInfo.dialysisMachine ?? ''} options={dialysisMachines.data} placeholder="Select Machine" ariaInvalid={Boolean(errors.dialysisMachine)} onChange={(dialysisMachine) => setAdmissionInfo({ ...admissionInfo, dialysisMachine: dialysisMachine || null })} /><FieldError message={errors.dialysisMachine} /></label>
            <label><FieldLabel required>Patient Type</FieldLabel><select value={insuranceType} onChange={(event) => setInsuranceType(event.target.value)} required aria-invalid={Boolean(errors.insuranceType)}><option value="">Select</option>{(tpas.data ?? []).map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}</select><FieldError message={errors.insuranceType} /></label>
            <label><FieldLabel>Insurance No</FieldLabel><input value={insuranceNumber} onChange={(event) => setInsuranceNumber(event.target.value)} maxLength={100} /></label>
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Patient Details</h2>
          <div className="ipd-daycare-patient-row1">
            <label><FieldLabel>UHID</FieldLabel><PatientUhidAutocomplete id="dialysis-patient" value={patientSearch} selectedId={patient.patientId} options={patients.data} isFetching={patients.isFetching} isError={patients.isError} onInputChange={changePatientSearch} onChange={choosePatient} /></label>
            <PatientIdentityFields value={patient} onChange={setPatient} errors={errors} />
          </div>
          <div className="ipd-daycare-patient-row2">
            <PatientDemographicFields value={patient} onChange={setPatient} states={states.data} districts={districts.data} errors={errors} />
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Patient Admission Information</h2>
          <div className="ipd-daycare-admission-row dialysis-admission-row">
            <AdmissionInfoFields
              value={admissionInfo}
              onChange={setAdmissionInfo}
              doctors={doctors.data}
              wards={wards.data}
              beds={beds.data}
              referrals={referrals.data}
              bedCategoryReadOnly
              hideMachine={true}
              errors={errors}
            />
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Responsible Person Details</h2>
          <div className="ipd-daycare-responsible-row">
            <ResponsiblePersonFields value={responsiblePerson} onChange={setResponsiblePerson} errors={errors} />
          </div>
        </section>
        <div className="ipd-daycare-form-actions"><button className="button button-primary" type="submit" disabled={busy}>{busy ? 'Saving...' : 'Save'}</button></div>
      </form>
    </div>
  );
};
